import React from "react";
import { getJobHistoryEntries } from "../utils/jobHistoryUtils";
import { useLanguage, TranslatedText, localeFor } from "../i18n";

// Reusable "History" popup: shows every note and supervisor note ever added
// to `job`, plus any job connected to it (Continue jobs, Extend From
// Existing WO Stem jobs), as one running record. Drop this into any page's
// Job Details popup alongside a "History" button — pass the currently
// selected job and that page's full jobs list, and it renders identically
// everywhere.
const JobHistoryPopup = ({ job, jobs = [], onClose }) => {
  const { t, lang } = useLanguage();
  if (!job) return null;

  const entries = getJobHistoryEntries(job, jobs);

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div
        className="modal"
        style={{
          width: "min(700px, 96vw)",
          maxHeight: "calc(100vh - 40px)",
          overflowY: "auto",
          textAlign: "left",
        }}
      >
        <h2 style={{ marginBottom: 4 }}>{t("Job History")}</h2>
        <p style={{ marginTop: 0, color: "#475569", fontSize: 13 }}>
          {t("All notes and supervisor notes for WO {wo}", { wo: job.wo || job.id })}
          {job.wo ? t(", and any jobs connected to it (Continue / Extend From Existing WO Stem).") : "."}
        </p>

        {entries.length === 0 && (
          <p style={{ fontStyle: "italic" }}>{t("No notes have been added yet.")}</p>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          {entries.map((entry, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: 12,
                background: entry.type === "Supervisor Note" ? "#f5f3ff" : "#f8fafc",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 6,
                  fontSize: 12,
                  color: "#475569",
                  marginBottom: 6,
                  fontWeight: 700,
                }}
              >
                <span>{t(entry.type)}</span>
                <span>
                  WO {entry.wo}
                  {entry.occurrenceDate ? ` • ${entry.occurrenceDate}` : ""}
                </span>
              </div>

              <div style={{ whiteSpace: "pre-wrap" }}>
                <TranslatedText text={entry.text} hint={entry.lang} />
              </div>

              <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                {entry.author}
                {entry.authorRole ? ` (${entry.authorRole})` : ""}
                {entry.date ? ` — ${new Date(entry.date).toLocaleString(localeFor(lang))}` : ""}
              </div>

              {Array.isArray(entry.files) && entry.files.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {entry.files.map((file, fileIndex) => (
                    <a
                      key={fileIndex}
                      href={file.fileURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 12 }}
                    >
                      {file.fileName || t("File")}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="modal-buttons">
          <button className="red-button" onClick={onClose}>
            {t("Close")}
          </button>
        </div>
      </div>
    </>
  );
};

export default JobHistoryPopup;
